import React, { useState, useEffect } from 'react';
import { Badge, Button, Icon, Avatar } from '../ui';
import { TransactionType } from '../../context/WalletContext';

type TransactionStatus = 'completed' | 'pending' | 'failed';

interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  date: string;
  description: string;
  status: TransactionStatus;
  reference?: string;
}

interface TransactionsListProps {
  transactions: Transaction[];
  isLoading?: boolean;
  onRefresh?: () => void;
  onDownloadReceipt?: (transactionId: string, format: 'pdf' | 'xlsx') => Promise<void>;
  downloadingTransactionId?: string | null;
}

type Tone = 'default' | 'ok' | 'warn' | 'err' | 'info' | 'navy' | 'cyan';

const statusTone: Record<TransactionStatus, Tone> = {
  completed: 'ok',
  pending: 'warn',
  failed: 'err',
};

const typeColor: Record<string, string> = {
  deposit: 'var(--ok)',
  refund: 'var(--ok)',
  withdrawal: 'var(--err)',
  payment: 'var(--err)',
  transfer: 'var(--brand-blue)',
};

const typeIcon: Record<string, string> = {
  deposit: 'arrowRight',
  refund: 'arrowRight',
  withdrawal: 'arrowLeft',
  payment: 'dollar',
  transfer: 'arrowRight',
  all: 'receipt',
};

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(amount);
}

function formatDate(dateString: string) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(dateString));
}

const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  isLoading = false,
  onRefresh,
  onDownloadReceipt,
  downloadingTransactionId,
}) => {
  const [filterType, setFilterType] = useState<TransactionType>('all');
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (!transactions || transactions.length === 0) { setFilteredTransactions([]); return; }
    setFilteredTransactions(filterType === 'all' ? transactions : transactions.filter(t => t.type === filterType));
  }, [transactions, filterType]);

  if (!transactions || transactions.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 12 }}>
        <Icon name="receipt" size={48} style={{ color: 'var(--ink-4)' }} />
        <p style={{ fontWeight: 600, fontSize: 15, margin: 0 }}>No transactions found</p>
        <p className="muted" style={{ margin: 0 }}>You haven't made any transactions yet</p>
        {onRefresh && (
          <Button variant="primary" onClick={onRefresh} style={{ marginTop: 4 }}>
            <Icon name="refresh" size={14} style={{ marginRight: 6 }} />
            Refresh Transactions
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="tx-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12 }}>
        <h3 style={{ fontWeight: 700, margin: 0, fontSize: 16 }}>Recent Transactions</h3>
        <select
          className="input tx-filter"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as TransactionType)}
          style={{ width: 160, fontSize: 13 }}
        >
          <option value="all">All Types</option>
          <option value="deposit">Deposits</option>
          <option value="withdrawal">Withdrawals</option>
          <option value="payment">Payments</option>
          <option value="refund">Refunds</option>
          <option value="transfer">Transfers</option>
        </select>
      </div>

      {filteredTransactions.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', gap: 8 }}>
          <Icon name="receipt" size={48} style={{ color: 'var(--ink-4)' }} />
          <p className="muted" style={{ margin: 0 }}>
            {filterType === 'all' ? "No transactions yet" : `No ${filterType} transactions found`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredTransactions.map((transaction) => {
            const isCredit = transaction.type === 'deposit' || transaction.type === 'refund';
            const color = typeColor[transaction.type] || 'var(--ink)';
            return (
              <div
                key={transaction.id}
                className="tx-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  background: 'var(--bg-elev)',
                  transition: 'transform 0.15s',
                }}
              >
                <Avatar className="tx-avatar" style={{ background: `${color}18`, flexShrink: 0 }}>
                  <Icon name={typeIcon[transaction.type] as any} size={18} style={{ color }} />
                </Avatar>

                <div className="tx-main" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{transaction.description}</div>
                  <div className="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatDate(transaction.date)}
                    {transaction.reference && ` • Ref: ${transaction.reference}`}
                  </div>
                </div>

                <div className="tx-side" style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color, marginBottom: 4 }}>
                    {isCredit ? '+' : '-'}{formatCurrency(transaction.amount)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    <Badge tone={statusTone[transaction.status]} style={{ fontSize: 11 }}>
                      {transaction.status}
                    </Badge>
                    {onDownloadReceipt && (
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          title="Download Receipt"
                          disabled={downloadingTransactionId === transaction.id}
                          onClick={() => setOpenMenuId(openMenuId === transaction.id ? null : transaction.id)}
                          style={{ width: 28, height: 28 }}
                        >
                          {downloadingTransactionId === transaction.id ? (
                            <span style={{ fontSize: 10 }}>...</span>
                          ) : (
                            <Icon name="download" size={14} />
                          )}
                        </button>
                        {openMenuId === transaction.id && (
                          <div
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: 4,
                              background: 'var(--bg-elev)',
                              border: '1px solid var(--line)',
                              borderRadius: 8,
                              boxShadow: 'var(--shd-2)',
                              zIndex: 100,
                              minWidth: 160,
                            }}
                          >
                            <button
                              type="button"
                              style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink)' }}
                              onClick={() => { onDownloadReceipt(transaction.id, 'xlsx'); setOpenMenuId(null); }}
                            >
                              <Icon name="download" size={13} style={{ color: 'var(--ok)' }} />
                              Download as Excel
                            </button>
                            <button
                              type="button"
                              style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink)' }}
                              onClick={() => { onDownloadReceipt(transaction.id, 'pdf'); setOpenMenuId(null); }}
                            >
                              <Icon name="file" size={13} style={{ color: 'var(--err)' }} />
                              Download as PDF
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransactionsList;
